import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { InferType } from 'yup';
import { buildEditProgramSchema, formShapeSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useChangeProgramStatusMutation,
  usePatientProfileQuery,
  useProgramStatusHistoryQuery,
} from '@common/cache';
import { paths } from '@common/types';
import dayjs from 'dayjs';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useConsent } from './useConsent';

type IFormShape = InferType<typeof formShapeSchema>;

const today = dayjs(new Date()).format('MMM DD, YYYY');

const initialValues: IFormShape = {
  tier: '',
  alignment_date: today,
  alignment_notification_date: today,
  unalignment_date: today,
  new_status: 'Unaligned',
  respite_eligible: 'Unknown',
};

interface IUseEditProgramDetailsProps {
  patient_id: string;
  program_id: string;
  programName: string;
  handleClose: () => void;
}

export const useEditProgramDetails = ({
  patient_id,
  program_id,
  programName,
  handleClose,
}: IUseEditProgramDetailsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState('1');
  const [tabIndexToChange, setTabIndexToChange] = useState('1');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConsentSaveModalOpen, setIsConsentSaveModalOpen] = useState(false);
  const [pendingCloseFromConsent, setPendingCloseFromConsent] = useState(false);

  const consent = useConsent({ program_id, patient_id, programName });

  const { data: programDetails, isPending: isProgramDetailsPending } =
    useProgramStatusHistoryQuery({
      patient_id,
      program_id,
    });
  const { data: patientProfile } = usePatientProfileQuery({ patient_id });

  const tierOptions = programDetails?.tier_options || [];
  const history = programDetails?.status_history || [];
  const currentStatus = programDetails?.current_status;
  const programStatusOptions = programDetails?.status_options || [];
  const unalignmentReasons = (
    programDetails?.unalignment_reason_options || []
  ).sort((a, b) => a.localeCompare(b));

  const ineligibilityReasons = [
    'Due to the tier',
    'Due to FAST score',
    'No ZBI Information',
  ];

  const editProgramSchema = useMemo(
    () =>
      buildEditProgramSchema(tierOptions.length > 0, programName === 'GUIDE'),
    [tierOptions, programName],
  );

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(editProgramSchema),
    defaultValues: {
      ...initialValues,
      respite_eligible: patientProfile?.respite_eligible || 'Unknown',
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    reset,
  } = formMethods;

  const new_status = useWatch({
    control: formMethods.control,
    name: 'new_status',
  });

  const handleReset = useCallback(() => {
    reset({
      tier: programDetails?.current_tier || '',
      alignment_date: programDetails?.alignment_date || today,
      alignment_notification_date:
        programDetails?.alignment_notification_date || today,
      unalignment_date: today,
      respite_eligible: patientProfile?.respite_eligible || 'Unknown',
      respite_ineligibility_reason:
        patientProfile?.respite_ineligibility_reason,
    });
  }, [patientProfile]);

  const { mutateAsync, isPending } = useChangeProgramStatusMutation({
    patient_id,
    program_id,
  });

  const handleTabChange = (event: React.SyntheticEvent, newIndex: string) => {
    setTabIndexToChange(newIndex);
    if (tabIndex === '3') {
      if (consent.isDirty) {
        setIsConsentSaveModalOpen(true);
      } else {
        setTabIndex(newIndex);
      }
    } else if (new_status) {
      setIsSaveChangesModalOpen(true);
    } else {
      setTabIndex(newIndex);
    }
  };

  // Route the modal close (X / backdrop / CONSENT tab Close button) through an
  // unsaved-changes guard when the CONSENT tab is active.
  const handleRequestClose = () => {
    if (tabIndex === '3' && consent.isDirty) {
      setPendingCloseFromConsent(true);
      setIsConsentSaveModalOpen(true);
    } else {
      handleClose();
    }
  };

  const finishConsentTransition = () => {
    if (pendingCloseFromConsent) {
      setPendingCloseFromConsent(false);
      handleClose();
    } else if (tabIndexToChange !== tabIndex) {
      setTabIndex(tabIndexToChange);
    }
  };

  const handleConsentSave = async () => {
    const ok = await consent.submit();
    setIsConsentSaveModalOpen(false);
    if (ok) {
      finishConsentTransition();
    }
  };

  const handleConsentDiscard = () => {
    consent.resetForm();
    setIsConsentSaveModalOpen(false);
    finishConsentTransition();
  };

  const handleConsentModalClose = () => {
    setIsConsentSaveModalOpen(false);
    setPendingCloseFromConsent(false);
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    if (
      (values.new_status === 'Unaligned' ||
        (programName === 'GUIDE' && values.new_status === 'To be unaligned')) &&
      !isConfirmModalOpen
    ) {
      return setIsConfirmModalOpen(true);
    }
    try {
      await mutateAsync({
        new_status:
          values.new_status as paths['/program/{program_id}/{patient_id}/change-status']['post']['requestBody']['content']['application/json']['new_status'],
        ...(values.new_status === 'Aligned' && {
          tier: values.tier,
        }),
        ...(values.new_status === 'Aligned' && {
          alignment_date: new Date(values.alignment_date).toISOString(),
        }),
        ...(values.new_status === 'Aligned' && {
          respite_eligible: values.respite_eligible,
          ...(values.respite_eligible === 'Ineligible' && {
            respite_ineligibility_reason: values.respite_ineligibility_reason,
          }),
        }),
        ...(values.new_status === 'Aligned' && {
          alignment_notification_date: new Date(
            values.alignment_notification_date,
          ).toISOString(),
        }),
        ...((values.new_status === 'Unaligned' ||
          values.new_status === 'To be unaligned') && {
          unalignment_reason: values.unalignment_reason,
        }),
        ...((values.new_status === 'Unaligned' ||
          values.new_status === 'To be unaligned') && {
          unalignment_date: new Date(values.unalignment_date).toISOString(),
        }),
        ...((values.new_status === 'Rejected' ||
          values.new_status === 'Resubmission rejected') && {
          rejection_reason: values.rejection_reason,
        }),
      });

      toast.success('Program edited successfully');
      setIsSaveChangesModalOpen(false);
      setIsConfirmModalOpen(false);
      handleReset();
      if (tabIndexToChange !== tabIndex) {
        setTabIndex(tabIndexToChange);
      } else {
        setIsOpen(false);
        setTabIndex('1');
        setTabIndexToChange('1');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  useEffect(() => {
    handleReset();
  }, [programDetails, patientProfile]);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleReset();
    if (tabIndexToChange !== tabIndex) {
      setTabIndex(tabIndexToChange);
    } else {
      setIsOpen(false);
      setTabIndex('1');
      setTabIndexToChange('1');
    }
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  return {
    formMethods,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    programStatusOptions,
    unalignmentReasons,
    ineligibilityReasons,
    tierOptions,
    history,
    programDetails,
    currentStatus,
    isValid,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isEditSubmitting: isPending,
    tabIndex,
    handleTabChange,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    isProgramDetailsPending,
    consent,
    handleRequestClose,
    isConsentSaveModalOpen,
    handleConsentSave,
    handleConsentDiscard,
    handleConsentModalClose,
  };
};
